import { InputSummary } from '../input-summary';
import { Report } from '../report/report.model';
import { Results } from '../results/results.model';
import { ScenarioComparison } from '../results/scenario-comparison/scenario-comparison.model';
import { Feedstock } from './feedstock/feedstock.model';
import { ProjectFinancial } from './financial/financial.model';
import { GhgEmissions } from './ghg-emission/ghg-emissions.model';
import { PlantSpecifications } from './plant-specifications/plant-specifications.model';
import { ProjectInfo } from './project-info.model';
import { SimulationProgress } from '../results/sim-progress/simulation-progress.model';
import { Sensitivity } from '../results/sensitivity/sensitivity.model';

export interface ProjectInterface {
  _id: string;
  accountID: string;
  projectInfo: ProjectInfo;
  feedstocks: Feedstock[];
  plantSpecifications: PlantSpecifications;
  financial: ProjectFinancial;
  ghgEmissions: GhgEmissions;
  sensitivity: Sensitivity;
  scenarioComparison: ScenarioComparison;
  results?: Results;
  reports: Report[];
  simProgress: SimulationProgress;
  dateLastTouched: Date;
  version: number;
  timeStamp: number
}

export class Project {
  public _id: string;
  public accountID: string;
  public projectInfo: ProjectInfo;
  public feedstocks: Feedstock[];
  public plantSpecifications: PlantSpecifications;
  public financial: ProjectFinancial;
  public ghgEmissions: GhgEmissions;
  public sensitivity: Sensitivity;
  public scenarioComparison: ScenarioComparison;
  public results: Results;
  public reports: Report[];
  public simProgress: SimulationProgress;
  public dateLastTouched: Date;
  public version: number;
  public timeStamp: number


  constructor(param: ProjectInterface = {
    _id: '',
    accountID: '',
    projectInfo: new ProjectInfo(),
    feedstocks: [],
    plantSpecifications: new PlantSpecifications(),
    financial: new ProjectFinancial(),
    ghgEmissions: new GhgEmissions(),
    sensitivity: new Sensitivity(),
    scenarioComparison: new ScenarioComparison(),
    reports: [],
    simProgress: new SimulationProgress(),
    dateLastTouched: null,
    version: null,
    timeStamp:null

  }) {
    this._id = param._id;
    this.accountID = param.accountID;
    this.projectInfo = new ProjectInfo(param.projectInfo);
    this.feedstocks = [];
    this.timeStamp = Date.now()
    if (param.feedstocks) {
      for (const feedstock of param.feedstocks) {
        this.feedstocks.push(new Feedstock(feedstock));
      }
    }
    this.plantSpecifications = new PlantSpecifications(param.plantSpecifications);
    this.financial = new ProjectFinancial(param.financial);
    this.ghgEmissions = new GhgEmissions(param.ghgEmissions);
    this.sensitivity = new Sensitivity(param.sensitivity);
    this.scenarioComparison = new ScenarioComparison(param.scenarioComparison);
    this.results = param.results ? new Results(param.results) : new Results();
    this.reports = [];
    if (param.reports) {
      for (const report of param.reports) {
        this.reports.push(new Report(report));
      }
    }
    if (param.simProgress) this.simProgress = param.simProgress;
    if (param.dateLastTouched) this.dateLastTouched = new Date(param.dateLastTouched);

    if(param.version) {
      this.version = param.version;
      console.log('version: ' + this.version);
    }
  }

  static deepCopy(project: Project) {
    let copy;
    if (project) {
      copy = new Project(project);
      return copy;
    }
    throw new Error('Project copy failed');
  }

  getInputSummary(hideDisabled?: boolean, isProjectInput?: boolean) {
    const inputSummary = {};
    if (!isProjectInput) {
      inputSummary[ProjectPropName.projectInfo] = this.projectInfo.getInputSummary(['projectInfo']);
    }
    inputSummary[ProjectPropName.feedstock] = this.getFeedstockInputSummary(isProjectInput);
    inputSummary[ProjectPropName.plantSpecifications] = this.plantSpecifications.getInputSummary(['plantSpecifications']);
    if (!hideDisabled || this.projectInfo.useFinancial) {
      inputSummary[ProjectPropName.financial] = this.financial.getInputSummary(this.feedstocks, this.plantSpecifications);
    }
    if (!isProjectInput && (!hideDisabled || this.projectInfo.useGHG)) {
      if (this.ghgEmissions) {
        inputSummary[ProjectPropName.ghgEmissions] = this.ghgEmissions.getInputSummary(['ghgEmissions']);
      }
    }
    return inputSummary;
  }


  getFeedstockInputSummary(isProjectInput?: boolean) {
    const inputSummary = new InputSummary();
    for (const material of this.feedstocks) {
      const index = this.feedstocks.indexOf(material);
      inputSummary.subSection[material.name] = material.getInputSummary(['feedstocks', String(index)], isProjectInput);
    }
    return inputSummary;
  }
}

export enum ProjectPropName {
  projectInfo = 'INPUT_SUMMARY.PROJECT.PROJECT_INFO',
  feedstock = 'INPUT_SUMMARY.PROJECT.FEEDSTOCK',
  plantSpecifications = 'INPUT_SUMMARY.PROJECT.PLANT_SPECIFICATION',
  financial = 'INPUT_SUMMARY.PROJECT.FINANCIAL',
  ghgEmissions = 'INPUT_SUMMARY.PROJECT.GHG_EMISSIONS',
  id_projectInfo = 'project info',
  id_feedstock = 'feedstock',
  id_plantSpecifications = 'plant specifications',
  id_financial = 'financial',
  id_ghgEmissions = 'greenhouse gas emissions',
}
